import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Dealer } from "@/types/dealer";
import { useGeolocation } from "@/hooks/useGeolocation";
import { RecommendedDealersHook } from "./types";
import { useInitialDataEffect, useCoordinatesEffect } from "./hooks";
import { refreshDealersData } from "./refreshData";
import { checkCacheVersion } from "./cache";
import { enhancedDealersCache } from "@/lib/recommended-dealers-cache/enhancedCache";
import { supabaseCustomRole } from "@/lib/supabase/customRoleClient";

// Implementazione debounce personalizzata per evitare dipendenze esterne
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Core hook for recommended dealers functionality
 * @param selectedMetal The metal type to get recommendations for
 * @returns Dealers data, loading state, error state, and refresh function
 */
export function useRecommendedDealers(selectedMetal: string = "ORO"): RecommendedDealersHook {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  
  // Anti-loop protection
  const refreshCount = useRef(0);
  const lastRefreshTime = useRef(0);
  const mounted = useRef(true);
  const lastMetal = useRef<string | null>(null);
  const cacheAge = useRef(0);
  const MAX_CACHE_AGE = 300000; // 5 minuti
  
  const { coordinates } = useGeolocation({ 
    showSuccessToast: false,
    enableHighAccuracy: false, // Disabilita high accuracy per velocità
    timeout: 3000, // Timeout aggressivo di 3 secondi
    maximumAge: 600000 // Cache per 10 minuti
  });
  
  // Simplified mount effect
  useEffect(() => {
    mounted.current = true;
    checkCacheVersion();
    cacheAge.current = Date.now();
    
    return () => {
      mounted.current = false;
    };
  }, []);
  
  // Optimized metal change detection with enhanced cache
  useEffect(() => {
    if (lastMetal.current && lastMetal.current !== selectedMetal) {
      console.log("🔄 useRecommendedDealers: Metal changed from", lastMetal.current, "to", selectedMetal);
      
      // Controlla se abbiamo dati cached per il nuovo metallo
      const cachedData = coordinates ? enhancedDealersCache.get(selectedMetal, { lat: coordinates.latitude, lng: coordinates.longitude }) : null;
      if (cachedData && cachedData.length > 0) {
        console.log("💾 Found cached data for", selectedMetal, ":", cachedData.length, "dealers");
        setDealers(cachedData);
        setLoading(false);
        setIsError(false);
      } else {
        // Don't clear dealers immediately - let new data load first
        setLoading(true);
        setIsError(false);
      }
      
      cacheAge.current = Date.now();
    }
    lastMetal.current = selectedMetal;
  }, [selectedMetal, coordinates]);
  
  // Handle initial data loading
  const { 
    initialLoadDone, 
    freshDataLoading, 
    fetchController,
    prefetchAttempted 
  } = useInitialDataEffect(
    selectedMetal,
    coordinates,
    setDealers,
    setLoading,
    setIsError,
    setHasAttemptedLoad
  );
  
  // Handle coordinates changes (only after initial load)
  useCoordinatesEffect(
    coordinates,
    selectedMetal,
    initialLoadDone.current, 
    freshDataLoading,
    fetchController,
    setDealers
  );
  
  // Debounced refresh function to prevent multiple calls
  const debouncedRefreshData = useMemo(
    () => debounce(async () => {
      const now = Date.now();
      
      // Anti-loop protection: max 2 manual refreshes per 5 seconds
      if (now - lastRefreshTime.current < 5000) {
        refreshCount.current++;
        if (refreshCount.current > 2) {
          console.log("🚫 useRecommendedDealers: Too many manual refreshes, throttling");
          return;
        }
      } else {
        refreshCount.current = 1;
      }
      
      lastRefreshTime.current = now;
      
      console.log("🔄 useRecommendedDealers: Manual refresh initiated", {
        metal: selectedMetal,
        refreshCount: refreshCount.current,
        dealersCount: dealers.length
      });
      
      if (!mounted.current) {
        console.log("⚠️ useRecommendedDealers: Component not ready, skipping refresh");
        return;
      }
      
      try {
        await refreshDealersData(
          selectedMetal,
          coordinates,
          fetchController,
          setLoading,
          setIsError,
          setDealers
        );
      } catch (error) {
        console.error("❌ useRecommendedDealers: Error during manual refresh:", error);
      }
    }, 300),
    [selectedMetal, coordinates, fetchController, dealers.length]
  );
  
  // Manual refresh function with debouncing
  const refreshData = useCallback(async () => {
    await debouncedRefreshData();
  }, [debouncedRefreshData]);
  
  // Reset refresh count periodically
  useEffect(() => {
    const interval = setInterval(() => {
      refreshCount.current = 0;
    }, 30000); // Reset every 30 seconds
    
    return () => clearInterval(interval);
  }, []);
  
  // Fallback per visualizzazione se i dati non si caricano
  const shouldShowFallback = useMemo(() => {
    return !dealers.length && !loading && !isError && hasAttemptedLoad;
  }, [dealers.length, loading, isError, hasAttemptedLoad]);
  
  // Determina se mostrare errore solo per fallimenti reali di caricamento
  const shouldShowError = useMemo(() => {
    return isError && hasAttemptedLoad && !loading;
  }, [isError, hasAttemptedLoad, loading]);
  
  // Invalidazione cache intelligente
  const shouldInvalidateCache = useMemo(() => {
    const now = Date.now();
    return lastMetal.current !== selectedMetal || 
           (now - cacheAge.current) > MAX_CACHE_AGE;
  }, [selectedMetal]);
  
  // Log diagnostico per il debug
  useEffect(() => {
    console.log("🔍 useRecommendedDealers: State update", {
      dealersCount: dealers.length,
      loading,
      isError,
      selectedMetal,
      shouldShowFallback,
      shouldInvalidateCache,
      timestamp: new Date().toISOString()
    });
  }, [dealers.length, loading, isError, selectedMetal, shouldShowFallback, shouldInvalidateCache]);
  
  // Return with both naming patterns for compatibility
  return { 
    dealers, 
    loading, 
    isLoading: loading, // Alias for backward compatibility
    isError, 
    refreshData, 
    refresh: refreshData, // Alias for backward compatibility
    shouldShowFallback,
    shouldInvalidateCache,
    shouldShowError
  };
}
